import React, { useEffect, useRef, useState } from "react";
import { Transaction } from "./interface";
import { format } from "date-fns";
import { API_ROUTES } from "@/services/environment";
import { GET } from "@/services/api";
import { useAppSelector } from "@/hooks/hook";
import {
  convertCentsToDollars,
  Loader,
  USFormatePriceWithDecimal,
} from "@thdinc/hpf-shared";
import downloadIcon from "@/assets/icons/download.svg";
import chevronDown from "@/assets/icons/chevronDown.svg";
import chevronUp from "@/assets/icons/chevronUp.svg";
import useDownloadPdf from "@/hooks/usePdfDownload";
import Alert from "@/components/alert/Alert";

interface TransactionTable {
  // transactions: Transaction[];
  bookingId: string;
}

interface ShowDetails {
  activeInx: number;
  showDetail: boolean;
}

const TransactionTable: React.FC<TransactionTable> = ({ bookingId }) => {
  const { getBookingTransactionApi } = API_ROUTES;
  const { refreshToken } = useAppSelector((store) => store.user);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { loading, downloadMultiPagePdf, downloadStorePdf } = useDownloadPdf();
  const [showDetail, setShowDetails] = useState<ShowDetails>({
    activeInx: -1,
    showDetail: false,
  });
  const [open, setOpen] = useState<ShowDetails>({
    activeInx: -1,
    showDetail: false,
  });
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target as Node)) {
        setOpen({ activeInx: -1, showDetail: false });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    (async () => {
      const res = await GET(
        `${getBookingTransactionApi}/${bookingId}`,
        refreshToken
      );

      if (res.data.success) {
        setTransactions(res.data.data);
      }
    })();
  }, [bookingId]);
  const paymentInvoice = (paymentInvoiceId: string) => {
    console.log("Payment Invoice ID: ", paymentInvoiceId);
    setDownloadingId(paymentInvoiceId);
    downloadMultiPagePdf(paymentInvoiceId)
      .then((fileName) => {
        console.log(`${fileName} successfully downloaded!`);
        console.log("Downloaded for Payment Invoice ID: ", paymentInvoiceId);
        setDownloadingId(null);
        setOpen({ activeInx: -1, showDetail: false });
        Alert({ message: "Invoice downloaded" });
      })
      .catch((err) => {
        setDownloadingId(null);
        Alert({ type: 'error', message: "Failed to download invoice PDF" });
        console.log("Failed to download invoice PDF:", err);
      });
  };
  const storePaymentInvoice = (paymentInvoiceId: string) => {
    console.log("Payment Invoice ID: ", paymentInvoiceId);
    setDownloadingId(paymentInvoiceId);
    downloadStorePdf(paymentInvoiceId)
      .then((fileName) => {
        console.log(`${fileName} successfully downloaded!`);
        console.log("Downloaded for Payment Invoice ID: ", paymentInvoiceId);
        setDownloadingId(null);
        setOpen({ activeInx: -1, showDetail: false });
        Alert({ message: "Invoice downloaded" });
      })
      .catch((err) => {
        setDownloadingId(null);
        Alert({ type: 'error', message: "Failed to download invoice PDF" });
        console.log("Failed to download invoice PDF:", err);
      });
  };

  return (
    <div className="pb-10">
      <div className="relative overflow-x-auto   rounded-2xl mt-4 overflow-y-auto max-h-full ">
        {transactions.length > 0 ? (
          <table className="w-full text-sm text-left rtl:text-right bg-p-gray ">
            <thead className="text-xs text-gray-700 uppercase border-b">
              <tr>
                <th scope="col" className="px-2 sm:px-5  py-4 uppercase">
                  Date
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-36"
                >
                  Total Charged
                </th>

                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-36"
                >
                  Platform Fee
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-48"
                >
                  Amount Transferred
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-36"
                >
                  Transferred To
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-28"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-28"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-28"
                >
                  Details
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-5  py-4 uppercase min-w-28"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((item: Transaction, index) => {
                const {
                  amount,
                  application_charges,
                  hows,
                  date,
                  products,
                  transaction_id,
                  tax,
                  store_name
                } = item;
                return (
                  <>
                    <tr key={index} className=" 0   border-b">
                      <td className="px-2 sm:px-4 py-4 min-w-28 text-center">
                        {format(date, "MM-dd-yyyy")}
                      </td>
                      <td className="px-2 sm:px-4 py-4 min-w-28 text-center">
                        {amount && (
                          <p>
                            ${""}
                            {USFormatePriceWithDecimal(
                              convertCentsToDollars(amount?.toString())
                            )}
                          </p>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4  min-w-28 text-center">
                        <p>
                          {`${application_charges
                            ? `$${USFormatePriceWithDecimal(
                              convertCentsToDollars(
                                application_charges?.toString()
                              )
                            )}`
                            : "-"
                            }`}
                        </p>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm  text-center">
                        {item.amount_transfered ? (
                          <p>
                            ${""}
                            {USFormatePriceWithDecimal(
                              convertCentsToDollars(
                                item.amount_transfered?.toString()
                              )
                            )}
                          </p>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm text-center">
                        <p>{store_name ? store_name : hows ? "HOWS" : "BTC"}</p>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm min-w-48 ">
                        <p>{item.type}</p>
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm min-w-48 ">
                        <p>{item.description}</p>
                      </td>
                      <td className="px-2 sm:px-2 py-4 text-sm min-w-48 ">
                        <div className="flex flex-row items-center gap-1">
                          {products?.length > 0 ? (
                            <p>{"Show details"}</p>
                          ) : (
                            <p className="ml-8">-</p>
                          )}
                          {/* {products?.length > 0 ? <p>{'Show details'}</p> : <p>-</p>} */}
                          {products?.length > 0 && (
                            <img
                              src={
                                showDetail.showDetail &&
                                  showDetail.activeInx === index
                                  ? chevronUp
                                  : chevronDown
                              }
                              className="cursor-pointer"
                              onClick={() => {
                                setShowDetails((prev) => {
                                  if (
                                    prev.showDetail &&
                                    prev.activeInx === index
                                  ) {
                                    return { activeInx: -1, showDetail: false };
                                  }
                                  return { activeInx: index, showDetail: true };
                                });
                              }}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-10 py-4 text-sm item-center">
                        <div
                          className={`flex flex-row ${products?.length > 0 && "cursor-pointer"
                            }`}
                        >
                          {products?.length > 0 ? (
                            <>
                              {downloadingId === transaction_id ? (
                                <div className="-ml-[6px]">
                                  <Loader color="bg-red-500" />
                                </div>
                              ) : (
                                <div className="relative inline-block">
                                  {/* Toggle Button */}
                                  <div
                                    onClick={() => {
                                      setOpen((prev) => {
                                        if (prev.showDetail && prev.activeInx === index) {
                                          return { activeInx: -1, showDetail: false };
                                        }
                                        return { activeInx: index, showDetail: true };
                                      })
                                    }
                                    }
                                  >
                                    <img src={downloadIcon} alt="download" />
                                  </div>

                                  {/* Dropdown Menu */}
                                  {open.showDetail && open.activeInx === index && (
                                    <div
                                      ref={dropdownMenuRef}
                                      className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                                      <button
                                        type="button"
                                        onClick={() => products?.length > 0 && paymentInvoice(transaction_id)}
                                        className="w-full px-4 py-2 hover:bg-gray-100 text-left"
                                      >
                                        Customer Invoice
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => products?.length > 0 && storePaymentInvoice(transaction_id)}
                                        className="w-full px-4 py-2 hover:bg-gray-100 text-left"
                                      >
                                        Store Invoice
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>
                    </tr>
                    {showDetail.showDetail &&
                      showDetail.activeInx === index && (
                        <tr>
                          <td colSpan={9} className="w-full">
                            <div className="relative overflow-x-auto  rounded-md m-4 overflow-y-auto max-h-full border">
                              <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                                <thead className="text-xs text-[#606060] uppercase bg-[#E5E7EB]">
                                  <tr>
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      Spirit Type
                                    </th>
                                    <th
                                      scope="col"
                                      className=" sm:px-3 py-4 uppercase text-center"
                                    >
                                      Brand Name
                                    </th>
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      Bottle Cost
                                    </th>
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      Number of Bottles
                                    </th>{" "}
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      Sub Total
                                    </th>{" "}
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      TAX AMOUNT
                                    </th>{" "}
                                    <th
                                      scope="col"
                                      className=" sm:px-2  py-4 uppercase text-center"
                                    >
                                      Total Amount
                                    </th>{" "}
                                  </tr>
                                </thead>
                                <tbody>
                                  {products.map((item: any, index) => {
                                    return (
                                      <tr
                                        key={index}
                                        className="bg-white 0  border-b"
                                      >
                                        <td className="sm:px-7 py-4  min-w-32 text-center">
                                          {item?.product_type
                                            ? item.product_type
                                              .charAt(0)
                                              .toUpperCase() +
                                            item.product_type.slice(1)
                                            : "-"}
                                        </td>
                                        <td className="sm:px-3 py-4 min-w-32 text-center">
                                          {item?.name ? item?.name : '-'}
                                        </td>
                                        <td className="sm:px-2 py-4 min-w-32 text-center">
                                          <p>
                                            {item?.bottle_cost ? convertCentsToDollars(item?.bottle_cost) : '-'}
                                          </p>
                                        </td>
                                        <td className="sm:px-2 py-4 min-w-32 text-center">
                                          <p>{item?.number_of_bottles ? item?.number_of_bottles : '-'}</p>
                                        </td>
                                        <td className="sm:px-2 py-4 min-w-32 text-center">
                                          <p>
                                            {
                                              item?.bottle_cost ||
                                                item?.number_of_bottles ?
                                                convertCentsToDollars(
                                                  String(
                                                    item?.bottle_cost *
                                                    item?.number_of_bottles
                                                  )
                                                ) :
                                                '-'
                                            }
                                          </p>
                                        </td>
                                        <td className="sm:px-2 py-4 min-w-32 text-center">
                                          <p>{item?.tax ? convertCentsToDollars(String(item?.tax)) : '-'}</p>
                                        </td>
                                        <td className="sm:px-2 py-4 min-w-32 text-center">
                                          <p>
                                            {
                                              item?.bottle_cost ||
                                                item?.number_of_bottles || item?.tax ?
                                                convertCentsToDollars(
                                                  String(
                                                    Number(item?.bottle_cost *
                                                      item?.number_of_bottles) + item?.tax
                                                  )
                                                ) :
                                                '-'
                                            }
                                          </p>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                  </>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Loader color="bg-red-500" />
        )}
      </div>
    </div>
  );
};

export default TransactionTable;
